import type { LearningDevice, PatternFeedback } from 'definitions';
import type { LearningsPageResponse } from '@@/api/learnings/page/page-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useAuth } from 'hooks/useAuth';
import { useClient } from 'hooks/useClient';
import { can } from 'definitions/capabilities';
import { apiErrorMessage } from '../api-error';
import { useWebsites } from '../useWebsites';

/** Every device's page and each device view, for one website: one prefix, so one invalidation reaches all. */
export const learningsPageKey = (websiteId: number | string, device?: LearningDevice | null) =>
  device === undefined ? ['LEARNINGS_PAGE', websiteId] : ['LEARNINGS_PAGE', websiteId, device ?? 'all'];

type UseLearningsPageParams = {
  /** The evidence map and grid's device view; null for every device. */
  device: LearningDevice | null;
};

/**
 * The Learnings page's data and its actions, for the app's current website. Asks only when the
 * website has learnings on: the page route answers 404 otherwise, and the screen shows setup.
 *
 * Feedback and dismissing change what the page features, so both reload it. Drafting creates an
 * experiment and opens it.
 */
export const useLearningsPage = ({ device }: UseLearningsPageParams) => {
  const client = useClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { website } = useWebsites();
  const websiteId: number = website?.id ?? 0;
  const enabled = !!websiteId && website?.learningsEnabled === true;

  const { data, isLoading, isError, refetch } = useQuery<LearningsPageResponse>({
    queryKey: learningsPageKey(websiteId, device),
    enabled,
    queryFn: () =>
      client.get<LearningsPageResponse>(`/learnings/${websiteId}/page${device ? `?device=${device}` : ''}`),
    // A view of the page someone is reading: keep the last one on screen while a device switch loads.
    placeholderData: (previous) => previous,
  });

  const reload = () => queryClient.invalidateQueries({ queryKey: learningsPageKey(websiteId) });

  const feedback = useMutation({
    mutationFn: (params: { patternKey: string; feedback: PatternFeedback | null }) =>
      client.post(`/learnings/${websiteId}/patterns/feedback`, params),
    onSuccess: (_, params) => {
      void reload();
      if (params.feedback === 'known') toast.success('Marked as known. It moves below what you have not seen yet.');
      if (params.feedback === 'not-useful') toast.success('Hidden from the page. It raises no more questions.');
    },
    onError: (error) => toast.error(apiErrorMessage(error, 'Could not save that.')),
  });

  const dismiss = useMutation({
    mutationFn: (opportunityId: number) =>
      client.post(`/learnings/${websiteId}/opportunities/${opportunityId}/dismiss`, {}),
    onSuccess: () => void reload(),
    onError: (error) => toast.error(apiErrorMessage(error, 'Could not dismiss it.')),
  });

  const draft = useMutation({
    mutationFn: (opportunityId: number) =>
      client.post<{ experimentId: number }>(`/learnings/${websiteId}/opportunities/${opportunityId}/draft`, {}),
    onSuccess: ({ experimentId }) => {
      void reload();
      navigate(`/experiments/${experimentId}`);
    },
    onError: (error) => {
      // The evidence moved since the page loaded: show the question as it stands now.
      void reload();
      toast.error(apiErrorMessage(error, 'Could not draft the experiment.'));
    },
  });

  return {
    website,
    websiteId,
    enabled,
    page: data ?? null,
    isLoading: enabled && isLoading,
    isError,
    refetch,
    canDraft: can(user, 'experiments:create'),
    setFeedback: (patternKey: string, value: PatternFeedback | null) =>
      feedback.mutate({ patternKey, feedback: value }),
    isSavingFeedback: feedback.isPending,
    dismiss: (opportunityId: number) => dismiss.mutate(opportunityId),
    draft: (opportunityId: number) => draft.mutate(opportunityId),
    draftingId: draft.isPending ? (draft.variables ?? null) : null,
  };
};

export type LearningsPageManager = ReturnType<typeof useLearningsPage>;
